import {
  BEEF_AI_ACCESS_TOKEN,
  BEEF_AI_BASE_URL,
  beefAiGet,
} from "../common/utils/beefAiClient";
import { provName, toProv } from "../components/Analytics/PriceReports/provinceUtils";
import { extractWorkflowRunId } from "./locationMissingReport";

export const STREET_TEAM_FOLDER_NAME = "street-team";
export const STREET_TEAM_PAGE_SIZE = 2000;
export const STREET_TEAM_MAX_PAGES = 20;
export const STREET_TEAM_FETCH_CONCURRENCY = 4;

/** Directus returns this alias for `json(metadata, run)`. Nested JSON is not filterable. */
export const PRICE_HISTORY_RUN_JSON_FIELD = "json(metadata, run)";
export const PRICE_HISTORY_RUN_JSON_ALIAS = "metadata_run_json";

export const UNKNOWN_PROVINCE_KEY = "unknown";
export const UNKNOWN_PROVINCE_LABEL = "Unknown province";
export const UNKNOWN_RETAILER_LABEL = "Unknown";
export const UNKNOWN_CUT_LABEL = "Unknown cut";
export const UNKNOWN_UPLOADER_LABEL = "Unknown";

/** Bulk internal uploads. Left out of the leaderboard only. */
export const LEADERBOARD_OMITTED_UPLOADER_EMAILS = ["internal+cdnbeef@tangle.ca"];
export const LEADERBOARD_MIN_UPLOADS = 3;

const OMITTED_LEADERBOARD_EMAILS = new Set(
  LEADERBOARD_OMITTED_UPLOADER_EMAILS.map((email) => email.toLowerCase())
);

export function isOmittedLeaderboardUploader(uploader) {
  const email = String(uploader?.email || "").trim().toLowerCase();
  const key = String(uploader?.key || "").trim().toLowerCase();
  return OMITTED_LEADERBOARD_EMAILS.has(email) || OMITTED_LEADERBOARD_EMAILS.has(key);
}

const SOURCE_RUN_FIELDS = [
  "id",
  "run_id",
  "file_id.id",
  "file_id.folder.name",
  "file_id.uploaded_on",
  "file_id.uploaded_by.first_name",
  "file_id.uploaded_by.last_name",
  "file_id.uploaded_by.email",
];

const PRICE_HISTORY_FIELDS = [
  "id",
  "retailer",
  "province",
  "product_name",
  "raw_product_name",
  "product_id",
  "is_void",
  PRICE_HISTORY_RUN_JSON_FIELD,
];

export function streetTeamReportConfigured() {
  return Boolean(BEEF_AI_BASE_URL && BEEF_AI_ACCESS_TOKEN);
}

function compareCountThenLabel(left, right) {
  const countDelta = (Number(right.count) || 0) - (Number(left.count) || 0);
  if (countDelta !== 0) {
    return countDelta;
  }
  return String(left.label || "").localeCompare(String(right.label || ""), undefined, {
    sensitivity: "base",
  });
}

function formatUploaderName(uploadedBy) {
  if (!uploadedBy || typeof uploadedBy === "string") {
    return "";
  }
  return `${uploadedBy.first_name || ""} ${uploadedBy.last_name || ""}`.trim();
}

export function uploaderFromFile(uploadedBy) {
  const name = formatUploaderName(uploadedBy);
  const email =
    uploadedBy && typeof uploadedBy === "object" ? String(uploadedBy.email || "").trim() : "";
  const key = (email || name || "unknown").toLowerCase();
  const label = name || email || UNKNOWN_UPLOADER_LABEL;
  return { key, name, email, label };
}

export function isStreetTeamFolder(folder) {
  const name = folder && typeof folder === "object" ? String(folder.name || "").trim() : "";
  return name.toLowerCase() === STREET_TEAM_FOLDER_NAME;
}

export function normalizeSourceImage(row) {
  const file = row?.file_id;
  const fileId =
    file && typeof file === "object" ? String(file.id || "").trim() : String(file || "").trim();
  if (!fileId || !isStreetTeamFolder(file?.folder)) {
    return null;
  }

  return {
    fileId,
    runId: String(row?.run_id || "").trim(),
    uploadedOn: file && typeof file === "object" ? file.uploaded_on || "" : "",
    uploader: uploaderFromFile(file && typeof file === "object" ? file.uploaded_by : null),
  };
}

export function isVoidPriceHistoryRow(row) {
  const value = row?.is_void;
  return value === true || value === 1 || value === "true" || value === "1";
}

export function provinceFromPriceRow(row) {
  const raw = String(row?.province || "").trim();
  const code = toProv(raw);
  if (code) {
    return { key: code, label: provName(code) };
  }
  if (!raw) {
    return { key: UNKNOWN_PROVINCE_KEY, label: UNKNOWN_PROVINCE_LABEL };
  }
  return { key: raw.toLowerCase(), label: raw };
}

export function retailerFromPriceRow(row) {
  const label = String(row?.retailer || "").trim() || UNKNOWN_RETAILER_LABEL;
  const province = provinceFromPriceRow(row);
  return {
    key: `${province.key}|${label}`,
    label,
    provinceKey: province.key,
    provinceLabel: province.label,
  };
}

export function cutFromPriceRow(row) {
  const product = row?.product_id;
  const productId =
    product && typeof product === "object"
      ? String(product.id || "").trim()
      : typeof product === "string"
        ? product.trim()
        : "";
  const relatedName =
    product && typeof product === "object" ? String(product.name || "").trim() : "";
  const label =
    relatedName ||
    String(row?.product_name || "").trim() ||
    String(row?.raw_product_name || "").trim() ||
    UNKNOWN_CUT_LABEL;
  return { key: productId || label, label };
}

export function extractPriceHistoryRunId(row) {
  const fromJson = row?.[PRICE_HISTORY_RUN_JSON_ALIAS] ?? row?.[PRICE_HISTORY_RUN_JSON_FIELD];
  if (typeof fromJson === "string" && fromJson.trim()) {
    return fromJson.trim();
  }
  if (fromJson != null && typeof fromJson !== "object") {
    const asString = String(fromJson).trim();
    if (asString) {
      return asString;
    }
  }
  return extractWorkflowRunId(row?.metadata);
}

export function normalizePriceLink(row) {
  if (!row || isVoidPriceHistoryRow(row)) {
    return null;
  }
  const runId = extractPriceHistoryRunId(row);
  if (!runId) {
    return null;
  }
  return {
    runId,
    retailer: retailerFromPriceRow(row),
    cut: cutFromPriceRow(row),
  };
}

/**
 * One uploaded file is one image. Several `source_run` rows can share that file,
 * and several `price_history` rows can share a workflow run.
 *
 * @param {object[]} sourceRuns
 * @param {object[]} priceRows
 */
export function buildStreetTeamImages(sourceRuns = [], priceRows = []) {
  const byFile = new Map();

  for (const row of sourceRuns) {
    const image = normalizeSourceImage(row);
    if (!image) {
      continue;
    }

    const existing = byFile.get(image.fileId);
    if (!existing) {
      byFile.set(image.fileId, {
        fileId: image.fileId,
        uploadedOn: image.uploadedOn,
        uploader: image.uploader,
        runIds: new Set(image.runId ? [image.runId] : []),
        retailers: new Map(),
        cuts: new Map(),
        cutsByRetailer: new Map(),
        linked: false,
      });
      continue;
    }

    if (image.runId) {
      existing.runIds.add(image.runId);
    }
    if (!existing.uploadedOn && image.uploadedOn) {
      existing.uploadedOn = image.uploadedOn;
    }
  }

  const filesByRunId = new Map();
  for (const image of byFile.values()) {
    for (const runId of image.runIds) {
      const list = filesByRunId.get(runId) || [];
      list.push(image);
      filesByRunId.set(runId, list);
    }
  }

  for (const row of priceRows) {
    const link = normalizePriceLink(row);
    if (!link) {
      continue;
    }
    const images = filesByRunId.get(link.runId) || [];
    for (const image of images) {
      image.linked = true;
      if (!image.retailers.has(link.retailer.key) || image.retailers.get(link.retailer.key).label === UNKNOWN_RETAILER_LABEL) {
        image.retailers.set(link.retailer.key, link.retailer);
      }
      const currentCut = image.cuts.get(link.cut.key);
      if (!currentCut || currentCut.label === UNKNOWN_CUT_LABEL) {
        image.cuts.set(link.cut.key, link.cut);
      }
      rememberRetailerCut(image, link.retailer, link.cut);
    }
  }

  const images = Array.from(byFile.values());
  return {
    images,
    unmatchedImageCount: images.filter((image) => !image.linked).length,
  };
}

export function buildLeaderboard(images = []) {
  const countedImages = images.filter((image) => !isOmittedLeaderboardUploader(image.uploader));
  const byUploader = new Map();

  for (const image of countedImages) {
    const uploader = image.uploader || {};
    const key = uploader.key || "unknown";
    const existing = byUploader.get(key) || {
      id: key,
      label: uploader.label || UNKNOWN_UPLOADER_LABEL,
      name: uploader.name || "",
      email: uploader.email || "",
      count: 0,
      images: [],
    };
    existing.count += 1;
    existing.images.push(imageForUploader(image));
    byUploader.set(key, existing);
  }

  const rows = Array.from(byUploader.values())
    .filter((row) => row.count >= LEADERBOARD_MIN_UPLOADS)
    .map((row) => ({
      ...row,
      images: [...row.images].sort(compareUploadedOnDesc),
    }))
    .sort(compareCountThenLabel);
  const includedUploaders = new Set(rows.map((row) => row.id));

  return {
    rows,
    totalImages: countedImages.filter((image) =>
      includedUploaders.has(image.uploader?.key || "unknown")
    ).length,
    top: rows[0] || null,
  };
}

function rememberVisitor(group, image) {
  const uploader = image.uploader || {};
  const visitorKey = uploader.key || "unknown";
  if (!group.visitors.has(visitorKey)) {
    group.visitors.set(visitorKey, uploader.label || UNKNOWN_UPLOADER_LABEL);
  }
}

function rememberRetailerCut(image, retailer, cut) {
  const cuts = image.cutsByRetailer.get(retailer.key) || new Map();
  const current = cuts.get(cut.key);
  if (!current || current.label === UNKNOWN_CUT_LABEL) {
    cuts.set(cut.key, cut);
  }
  image.cutsByRetailer.set(retailer.key, cuts);
}

function cutLabels(cuts) {
  return Array.from(cuts || [])
    .map((cut) => String(cut?.label || "").trim())
    .filter(Boolean)
    .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
}

function imageForUploader(image) {
  const uploader = image.uploader || {};
  const retailers = Array.from(image.retailers?.values() || [])
    .map((retailer) => ({
      label: retailer.label,
      provinceLabel: retailer.provinceLabel || "",
    }))
    .sort((left, right) => {
      const provinceDelta = String(left.provinceLabel).localeCompare(String(right.provinceLabel), undefined, {
        sensitivity: "base",
      });
      if (provinceDelta !== 0) {
        return provinceDelta;
      }
      return String(left.label).localeCompare(String(right.label), undefined, { sensitivity: "base" });
    });

  return {
    fileId: image.fileId,
    uploadedOn: image.uploadedOn || "",
    uploaderLabel: uploader.label || UNKNOWN_UPLOADER_LABEL,
    uploaderEmail: uploader.email || "",
    retailers,
    cuts: cutLabels(image.cuts?.values()),
    caption: retailers
      .map((retailer) =>
        retailer.provinceLabel ? `${retailer.label}, ${retailer.provinceLabel}` : retailer.label
      )
      .join("; "),
  };
}

function imageForRetailer(image, retailerKey) {
  const uploader = image.uploader || {};
  return {
    fileId: image.fileId,
    uploadedOn: image.uploadedOn || "",
    uploaderLabel: uploader.label || UNKNOWN_UPLOADER_LABEL,
    uploaderEmail: uploader.email || "",
    cuts: cutLabels(image.cutsByRetailer?.get(retailerKey)?.values()),
  };
}

function compareUploadedOnDesc(left, right) {
  const leftTime = new Date(left.uploadedOn).getTime();
  const rightTime = new Date(right.uploadedOn).getTime();
  const leftValue = Number.isNaN(leftTime) ? 0 : leftTime;
  const rightValue = Number.isNaN(rightTime) ? 0 : rightTime;
  if (leftValue !== rightValue) {
    return rightValue - leftValue;
  }
  return String(left.fileId || "").localeCompare(String(right.fileId || ""));
}

/**
 * Count each linked photo once per retailer or beef cut, and collect who uploaded those photos.
 *
 * @param {ReturnType<typeof buildStreetTeamImages>["images"]} images
 * @param {"retailers" | "cuts"} groupKey
 */
export function buildVisitGroups(images = [], groupKey) {
  const groups = new Map();
  let linkedImageCount = 0;

  for (const image of images) {
    if (!image.linked) {
      continue;
    }
    const entries = image[groupKey];
    if (!entries || entries.size === 0) {
      continue;
    }
    linkedImageCount += 1;

    for (const entry of entries.values()) {
      const existing = groups.get(entry.key) || {
        id: entry.key,
        label: entry.label,
        provinceKey: entry.provinceKey || "",
        provinceLabel: entry.provinceLabel || "",
        count: 0,
        visitors: new Map(),
        images: [],
        cutCounts: new Map(),
      };
      if (existing.label === UNKNOWN_RETAILER_LABEL || existing.label === UNKNOWN_CUT_LABEL) {
        existing.label = entry.label;
      }
      existing.count += 1;
      rememberVisitor(existing, image);
      if (groupKey === "retailers") {
        existing.images.push(imageForRetailer(image, entry.key));
        const cuts = image.cutsByRetailer?.get(entry.key);
        if (cuts) {
          for (const cut of cuts.values()) {
            const current = existing.cutCounts.get(cut.key) || {
              id: cut.key,
              label: cut.label,
              count: 0,
            };
            if (current.label === UNKNOWN_CUT_LABEL) {
              current.label = cut.label;
            }
            current.count += 1;
            existing.cutCounts.set(cut.key, current);
          }
        }
      }
      groups.set(entry.key, existing);
    }
  }

  const rows = Array.from(groups.values())
    .filter((row) => row.count > 0)
    .map((row) => {
      const base = {
        id: row.id,
        label: row.label,
        provinceKey: row.provinceKey || "",
        provinceLabel: row.provinceLabel || "",
        count: row.count,
        visitors: Array.from(row.visitors.values()).sort((left, right) =>
          left.localeCompare(right, undefined, { sensitivity: "base" })
        ),
      };
      if (groupKey !== "retailers") {
        return base;
      }
      return {
        ...base,
        images: [...row.images].sort(compareUploadedOnDesc),
        cutCounts: Array.from(row.cutCounts.values()).sort(compareCountThenLabel),
      };
    })
    .sort(compareCountThenLabel);

  return {
    rows,
    provinces: groupKey === "retailers" ? groupRetailerRowsByProvince(rows) : [],
    linkedImageCount,
    top: rows[0] || null,
  };
}

function compareProvince(left, right) {
  const leftUnknown = left.id === UNKNOWN_PROVINCE_KEY;
  const rightUnknown = right.id === UNKNOWN_PROVINCE_KEY;
  if (leftUnknown !== rightUnknown) {
    return leftUnknown ? 1 : -1;
  }
  return String(left.label || "").localeCompare(String(right.label || ""), undefined, {
    sensitivity: "base",
  });
}

/**
 * Retailer rows grouped under their price-history province.
 * Retailers inside a province stay ordered by image count.
 *
 * @param {ReturnType<typeof buildVisitGroups>["rows"]} rows
 */
export function groupRetailerRowsByProvince(rows = []) {
  const provinces = new Map();

  for (const row of rows) {
    const key = row.provinceKey || UNKNOWN_PROVINCE_KEY;
    const existing = provinces.get(key) || {
      id: key,
      label: row.provinceLabel || UNKNOWN_PROVINCE_LABEL,
      count: 0,
      retailers: [],
    };
    existing.count += Number(row.count) || 0;
    existing.retailers.push(row);
    provinces.set(key, existing);
  }

  return Array.from(provinces.values())
    .map((province) => ({
      ...province,
      retailers: [...province.retailers].sort(compareCountThenLabel),
    }))
    .sort(compareProvince);
}

export function buildSourceRunQueryParams({ page = 1, dateFrom, dateTo } = {}) {
  const params = new URLSearchParams();
  params.set("access_token", BEEF_AI_ACCESS_TOKEN);
  params.set("page", String(page));
  params.set("limit", String(STREET_TEAM_PAGE_SIZE));
  params.set("sort", "id");
  SOURCE_RUN_FIELDS.forEach((field) => params.append("fields[]", field));
  params.set("filter[file_id][_nnull]", "true");
  params.set("filter[file_id][folder][name][_eq]", STREET_TEAM_FOLDER_NAME);
  if (dateFrom) {
    params.set("filter[file_id][uploaded_on][_gte]", dateFrom);
  }
  if (dateTo) {
    params.set("filter[file_id][uploaded_on][_lte]", dateTo);
  }
  return params;
}

export function buildPriceHistoryWindowParams({ page = 1, dateFrom, dateTo } = {}) {
  const params = new URLSearchParams();
  params.set("access_token", BEEF_AI_ACCESS_TOKEN);
  params.set("page", String(page));
  params.set("limit", String(STREET_TEAM_PAGE_SIZE));
  params.set("sort", "id");
  PRICE_HISTORY_FIELDS.forEach((field) => params.append("fields[]", field));
  params.set("filter[_and][0][_or][0][is_void][_null]", "true");
  params.set("filter[_and][0][_or][1][is_void][_eq]", "false");
  // record_at tracks the source photo time (uploaded_on). metadata.run is JSON and
  // cannot be filtered, so the run id is matched in the client after this page loads.
  if (dateFrom) {
    params.set("filter[_and][1][record_at][_gte]", dateFrom);
  }
  if (dateTo) {
    params.set("filter[_and][1][record_at][_lte]", dateTo);
  }
  return params;
}

function isWithinUploadedRange(uploadedOn, dateFrom, dateTo) {
  const time = new Date(uploadedOn).getTime();
  if (Number.isNaN(time)) {
    return false;
  }
  if (dateFrom && time < new Date(dateFrom).getTime()) {
    return false;
  }
  if (dateTo && time > new Date(dateTo).getTime()) {
    return false;
  }
  return true;
}

export function pageCountForFilter(filterCount, pageSize = STREET_TEAM_PAGE_SIZE) {
  const count = Number(filterCount);
  if (!Number.isFinite(count) || count <= 0) {
    return 0;
  }
  return Math.ceil(count / pageSize);
}

/**
 * Pages after the first. `truncated` is set when the filter is larger than the page cap.
 */
export function remainingPages(filterCount, pageSize = STREET_TEAM_PAGE_SIZE, maxPages = STREET_TEAM_MAX_PAGES) {
  const totalPages = pageCountForFilter(filterCount, pageSize);
  const lastPage = Math.min(totalPages, maxPages);
  const pages = [];
  for (let page = 2; page <= lastPage; page += 1) {
    pages.push(page);
  }
  return { totalPages, pages, truncated: totalPages > maxPages };
}

async function mapPool(items, limit, worker) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function run() {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await worker(items[index]);
    }
  }

  const workers = Math.min(limit, items.length);
  await Promise.all(Array.from({ length: workers }, () => run()));
  return results;
}

async function fetchCollectionPages(collection, buildParams) {
  const firstParams = buildParams({ page: 1 });
  firstParams.set("meta", "filter_count");
  const firstJson = await beefAiGet(`/items/${collection}?${firstParams.toString()}`);
  const rows = [...(firstJson?.data || [])];
  const filterCount = Number(firstJson?.meta?.filter_count);

  if (!Number.isFinite(filterCount)) {
    let truncated = false;
    for (let page = 2; page <= STREET_TEAM_MAX_PAGES; page += 1) {
      const params = buildParams({ page });
      const json = await beefAiGet(`/items/${collection}?${params.toString()}`);
      const batch = json?.data || [];
      rows.push(...batch);
      if (batch.length < STREET_TEAM_PAGE_SIZE) {
        return { rows, truncated };
      }
      if (page === STREET_TEAM_MAX_PAGES) {
        truncated = true;
      }
    }
    return { rows, truncated };
  }

  const plan = remainingPages(filterCount);
  const batches = await mapPool(plan.pages, STREET_TEAM_FETCH_CONCURRENCY, async (page) => {
    const params = buildParams({ page });
    const json = await beefAiGet(`/items/${collection}?${params.toString()}`);
    return json?.data || [];
  });

  return {
    rows: rows.concat(...batches),
    truncated: plan.truncated,
  };
}

async function fetchSourceRuns({ dateFrom, dateTo }) {
  const fetched = await fetchCollectionPages("source_run", (pageOptions) =>
    buildSourceRunQueryParams({ ...pageOptions, dateFrom, dateTo })
  );
  return {
    truncated: fetched.truncated,
    rows: fetched.rows.filter((row) => {
      const image = normalizeSourceImage(row);
      return image && isWithinUploadedRange(image.uploadedOn, dateFrom, dateTo);
    }),
  };
}

async function fetchPriceHistoryWindow({ dateFrom, dateTo }) {
  return fetchCollectionPages("price_history", (pageOptions) =>
    buildPriceHistoryWindowParams({ ...pageOptions, dateFrom, dateTo })
  );
}

/**
 * Uploaded source images in a date range, with retailer and beef-cut visits
 * taken from linked `price_history` rows (`metadata.run` = `source_run.run_id`).
 */
export async function fetchStreetTeamReport({ dateFrom, dateTo } = {}) {
  if (!streetTeamReportConfigured()) {
    throw new Error(
      "Street team reports are not configured. Set REACT_APP_PRICE_DIRECTUS_URL and REACT_APP_PRICE_DIRECTUS_TOKEN."
    );
  }

  const [source, prices] = await Promise.all([
    fetchSourceRuns({ dateFrom, dateTo }),
    fetchPriceHistoryWindow({ dateFrom, dateTo }),
  ]);
  const runIds = new Set(
    source.rows.map((row) => String(row?.run_id || "").trim()).filter(Boolean)
  );
  const linkedPrices = prices.rows.filter((row) => runIds.has(extractPriceHistoryRunId(row)));
  const built = buildStreetTeamImages(source.rows, linkedPrices);

  return {
    truncated: source.truncated || prices.truncated,
    unmatchedImageCount: built.unmatchedImageCount,
    leaderboard: buildLeaderboard(built.images),
    retailers: buildVisitGroups(built.images, "retailers"),
    cuts: buildVisitGroups(built.images, "cuts"),
  };
}
